/**
 * SearchResults Component
 * Displays a list of search results with virtual scrolling
 */

import { WordDefinition } from '@/types/dictionary';
import { SearchResult } from '@/types/search';
import { WordCard } from './WordCard';

interface SearchResultsProps {
  results: SearchResult[];
  definitions: Map<string, WordDefinition>;
  onLoadDefinition: (word: string) => void;
  onTagClick?: (term: string) => void;
  isLoading?: boolean;
  isLoadingMore?: boolean;
}

export function SearchResults({
  results,
  definitions,
  onLoadDefinition,
  onTagClick,
  isLoading = false,
  isLoadingMore = false,
}: SearchResultsProps) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-dictionary-accent"></div>
      </div>
    );
  }

  if (results.length === 0) {
    return (
      <div className="text-center py-12">
        <svg
          className="mx-auto h-12 w-12 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No results found</h3>
        <p className="mt-2 text-sm text-gray-500">
          Try adjusting your search or browse by theme
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-gray-600">
        <span>
          Found {results.length} {results.length === 1 ? 'word' : 'words'}
        </span>
        {results[0] && results[0].matchPosition > 1 && (
          <span className="text-blue-600">
            Searched by position {results[0].matchPosition}
          </span>
        )}
      </div>

      {/* Results List */}
      <div className="space-y-4">
        {results.map((result) => {
          const definition = definitions.get(result.word.word);

          // If we have a definition, show full WordCard
          if (definition) {
            return <WordCard key={result.word.xmlId || result.word.word} data={definition} onTagClick={onTagClick} />;
          }

          // Otherwise, create a basic card and trigger loading
          return (
            <div
              key={result.word.xmlId || result.word.word}
              className="bg-white dark:bg-slate-800 shadow-xl rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 p-6 cursor-pointer hover:shadow-2xl transition-shadow"
              onClick={() => onLoadDefinition(result.word.word)}
            >
              <div className="flex items-baseline gap-2">
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white">
                  {result.word.word}
                </h3>
                <span className="text-sm text-slate-500 font-mono">/{result.word.ipa}/</span>
              </div>
              <div className="mt-2 flex gap-2 flex-wrap">
                {result.word.themes.map((theme) => (
                  <span
                    key={theme}
                    onClick={(e) => {
                      e.stopPropagation();
                      onTagClick?.(theme);
                    }}
                    className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-300 hover:bg-blue-100 dark:hover:bg-blue-900 hover:text-blue-800 dark:hover:text-blue-200 cursor-pointer transition-colors"
                  >
                    {theme}
                  </span>
                ))}
              </div>
              <p className="mt-3 text-sm text-blue-600 dark:text-blue-400">
                Click to load full definition...
              </p>
            </div>
          );
        })}
      </div>

      {/* Progressive Loading Indicator */}
      {isLoadingMore && results.length > 0 && (
        <div className="flex items-center justify-center py-6 border-t border-slate-200 dark:border-slate-700">
          <div className="flex items-center gap-3 text-sm text-slate-600 dark:text-slate-400">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-500"></div>
            <span className="animate-pulse">Loading more results...</span>
          </div>
        </div>
      )}
    </div>
  );
}
